import { useEffect, useRef, useState } from 'react';
import './MercurXDateRangePicker.scss';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getMonthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const getDayStart = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date, dayCount) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + dayCount);
  return nextDate;
};

const areSameDay = (firstDate, secondDate) => {
  if (!firstDate || !secondDate) {
    return false;
  }

  return getDayStart(firstDate).getTime() === getDayStart(secondDate).getTime();
};

const isDateBetween = (date, startDate, endDate) => {
  if (!startDate || !endDate) {
    return false;
  }

  const currentTime = getDayStart(date).getTime();
  const startTime = getDayStart(startDate).getTime();
  const endTime = getDayStart(endDate).getTime();

  return currentTime > startTime && currentTime < endTime;
};

const getMonthDays = (visibleMonth) => {
  const monthStart = getMonthStart(visibleMonth);
  const firstVisibleDay = addDays(monthStart, -monthStart.getDay());

  return Array.from({ length: 42 }, (_, index) => addDays(firstVisibleDay, index));
};

const formatRangeDate = (date) => {
  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
};

function MercurXDateRangePicker({
  label,
  placeholder = 'MM/DD/YYYY - MM/DD/YYYY',
  startDate,
  endDate,
  onChange,
}) {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(
    getMonthStart(startDate || new Date())
  );

  useEffect(() => {
    const nextVisibleMonth = getMonthStart(startDate || endDate || new Date());
    setVisibleMonth(nextVisibleMonth);
  }, [startDate, endDate]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleDateClick = (selectedDate) => {
    const normalizedDate = getDayStart(selectedDate);

    if (!startDate || (startDate && endDate)) {
      onChange({
        startDate: normalizedDate,
        endDate: null,
      });
      return;
    }

    if (normalizedDate.getTime() < getDayStart(startDate).getTime()) {
      onChange({
        startDate: normalizedDate,
        endDate: getDayStart(startDate),
      });
      setIsOpen(false);
      return;
    }

    onChange({
      startDate: getDayStart(startDate),
      endDate: normalizedDate,
    });
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange({
      startDate: null,
      endDate: null,
    });
  };

  const displayValue = startDate || endDate
    ? `${formatRangeDate(startDate)}${endDate ? ` - ${formatRangeDate(endDate)}` : ' - ...'}`
    : placeholder;

  return (
    <div className="mercurx-date-range-picker" ref={containerRef}>
      {label && <div className="filter-control-label">{label}</div>}
      <button
        type="button"
        className={`mercurx-date-range-trigger filter-control-input${
          startDate || endDate ? ' has-value' : ''
        }`}
        onClick={() => setIsOpen((prevValue) => !prevValue)}
      >
        <span>{displayValue}</span>
        <span className="mercurx-date-range-trigger__chevron">
          {isOpen ? '▲' : '▼'}
        </span>
      </button>
      {isOpen && (
        <div className="mercurx-date-range-calendar">
          <div className="mercurx-date-range-calendar__header">
            <button
              type="button"
              className="mercurx-date-range-calendar__nav"
              onClick={() =>
                setVisibleMonth(
                  new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1)
                )
              }
            >
              ‹
            </button>
            <div className="mercurx-date-range-calendar__title">
              {visibleMonth.toLocaleString('en-US', {
                month: 'long',
                year: 'numeric',
              })}
            </div>
            <button
              type="button"
              className="mercurx-date-range-calendar__nav"
              onClick={() =>
                setVisibleMonth(
                  new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1)
                )
              }
            >
              ›
            </button>
          </div>
          <div className="mercurx-date-range-calendar__weekdays">
            {WEEKDAY_LABELS.map((weekday) => (
              <div key={weekday} className="mercurx-date-range-calendar__weekday">
                {weekday}
              </div>
            ))}
          </div>
          <div className="mercurx-date-range-calendar__days">
            {getMonthDays(visibleMonth).map((day) => {
              const isCurrentMonth = day.getMonth() === visibleMonth.getMonth();
              const isRangeStart = areSameDay(day, startDate);
              const isRangeEnd = areSameDay(day, endDate);
              const isInRange = isDateBetween(day, startDate, endDate);
              const isSingleDayRange = isRangeStart && isRangeEnd;

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  className={`mercurx-date-range-calendar__day${
                    isCurrentMonth ? '' : ' is-outside-month'
                  }${isRangeStart ? ' is-range-start' : ''}${
                    isRangeEnd ? ' is-range-end' : ''
                  }${isInRange ? ' is-in-range' : ''}${
                    isSingleDayRange ? ' is-single-day-range' : ''
                  }`}
                  onClick={() => handleDateClick(day)}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
          <div className="mercurx-date-range-calendar__footer">
            <div className="mercurx-date-range-calendar__summary">
              <span>Start: {formatRangeDate(startDate) || '--/--/----'}</span>
              <span>End: {formatRangeDate(endDate) || '--/--/----'}</span>
            </div>
            <button
              type="button"
              className="mercurx-date-range-calendar__clear"
              onClick={handleClear}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MercurXDateRangePicker;
